import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { vehicleRequestsApi } from '../../api/fleet';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { formatDate } from '../../utils/formatDate';

export const VEHICLE_REQUEST_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft',
  clarification_required: 'Additional Information Required', approved: 'Approved', rejected: 'Rejected',
  cancelled: 'Cancelled', in_progress: 'In Progress', completed: 'Completed', closed: 'Closed', archived: 'Archived',
};

interface CreateVehicleRequestForm {
  requesterEmployeeId: number; purpose: string; pickupLocation: string; destination: string; departureDate: string;
  expectedReturnDate: string; numberOfPassengers?: number; priority?: string;
}

export function VehicleRequestsListPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateVehicleRequestForm>();

  const { data, isLoading } = useQuery({ queryKey: ['vehicle-requests', statusFilter], queryFn: () => vehicleRequestsApi.list({ status: statusFilter || undefined }) });

  const createMutation = useMutation({
    mutationFn: (input: CreateVehicleRequestForm) => vehicleRequestsApi.create({ ...input, numberOfPassengers: input.numberOfPassengers ? Number(input.numberOfPassengers) : undefined }),
    onSuccess: (result) => {
      showSuccess(`Vehicle request ${result.data.requestReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['vehicle-requests'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/vehicle-requests/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const requests = data?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Vehicle Requests" description="Request an organization vehicle for a trip or mission." />

      <SectionHeader title="Requests" actions={
        <>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {Object.entries(VEHICLE_REQUEST_STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Vehicle Request'}</button>
        </>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Requester <EmployeePicker {...register('requesterEmployeeId', { required: true, valueAsNumber: true })} /></label>
          <label>Purpose <input {...register('purpose', { required: true })} /></label>
          <label>Pickup Location <input {...register('pickupLocation', { required: true })} /></label>
          <label>Destination <input {...register('destination', { required: true })} /></label>
          <label>Departure Date <input type="date" {...register('departureDate', { required: true })} /></label>
          <label>Expected Return <input type="date" {...register('expectedReturnDate', { required: true })} /></label>
          <label>Passengers <input type="number" {...register('numberOfPassengers')} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create Draft</button>
        </form>
      )}

      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={requests}
          rowKey={(r) => r.id}
          columns={[
            { header: 'Reference', render: (r) => <a onClick={() => navigate(`/logistics/vehicle-requests/${r.id}`)} className="table-link">{r.request_reference}</a> },
            { header: 'Purpose', render: (r) => r.purpose },
            { header: 'Route', render: (r) => `${r.pickup_location} → ${r.destination}` },
            { header: 'Departure', render: (r) => formatDate(r.departure_date) },
            { header: 'Return', render: (r) => formatDate(r.expected_return_date) },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={VEHICLE_REQUEST_STATUS_LABELS[r.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
